import React from 'react';
import _merge from 'lodash/merge';
import Tokens from '@nokia-csf-uxr/freeform-design-tokens';
import { getContext } from 'common';

import RippleEffect, { RippleEffectProps } from '../RippleEffect';
import ButtonCore from '../ButtonCore';
import Styled from './styles';
import ButtonIcon from './ButtonIcon';
import ButtonText from './ButtonText';

const {
  RADIUS,
} = Tokens;

interface ButtonProps extends React.ComponentPropsWithRef<'button'> {
  /** The variant of the button. "secondary" is identical with "default", please use "secondary" instead of "default" as "default" will be deprecated soon. */
  variant?: 'secondary' | 'default' | 'call-to-action' | 'outlined';
  /** The content of the button including `ButtonText` and `ButtonIcon`. If the content is only one `ButtonIcon` then `IconButton` component should be used. */
  children: React.ReactNode;
  /** If `true`, the button will be disabled. */
  disabled?: boolean;
  /** If `true`, the button will be rendered without ripple effect animation. */
  disabledRipple?: boolean;
  /** Ripple style. (The outermost element is RippleEffect.) */
  rippleStyle?: React.CSSProperties;
  /** To alter the ripple effect DOM element (the outermost element) attributes. */
  rippleProps?: Partial<RippleEffectProps>;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** Expands the button to fit the width of it's container. */
  fullWidth?: boolean;
}

/**
 * Any other props than what documented below (e.g., `onClick`) will be provided to the button element.
 *
 * To alter the outermost element attributes use `rippleProps` prop.
 *
 * To get the ref of the button element use `ref` prop. [Read React useRef hook documentation](https://reactjs.org/docs/hooks-reference.
 * */
const Button = React.forwardRef<HTMLElement, ButtonProps>((props: ButtonProps, ref) => getContext(({ theme }) => {
  const {
    children,
    disabledRipple,
    rippleStyle,
    disabled,
    variant,
    fullWidth,
    theme: propTheme,
    rippleProps,
    ...otherProps
  } = props;

  const resolvedTheme = _merge(
    {},
    variant === 'call-to-action' ? theme.BUTTON.CTA : variant === 'outlined' ? theme.BUTTON.OUTLINED : theme.BUTTON.FLAT,
    propTheme
  );

  const resolvedWrapper = variant === 'call-to-action' ? Styled.CTAButton : variant === 'outlined' ? Styled.OutlinedButton : Styled.Button;

  return (
    <RippleEffect
      disabled={disabledRipple || disabled}
      rippleStyle={{
        backgroundColor: resolvedTheme.rippleColor,
      }}
      style={{
        borderRadius: RADIUS.RADIUS_2,
        flex: '0 1 auto',
        width: fullWidth ? '100%' : 'min-content',
        ...rippleStyle
      }}
      {...rippleProps}
    >
      <ButtonCore
        ref={ref}
        $theme={resolvedTheme}
        theme={resolvedTheme}
        Wrapper={resolvedWrapper}
        disabled={disabled}
        variant={variant}
        fullWidth={fullWidth}
        {...otherProps}
      >
        {children}
      </ButtonCore>
    </RippleEffect>
  );
}));

Button.displayName = 'Button';

Button.defaultProps = {
  variant: 'secondary',
  disabled: false,
  disabledRipple: false,
  rippleStyle: {},
  rippleProps: {},
  theme: {},
  fullWidth: false
};

export {
  Button as default,
  ButtonIcon,
  ButtonText,
  ButtonProps
};
